import { useCallback, useState } from "react";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";
import {
    ASSESSMENT_DEFAULT_PAGE_SIZE,
    buildAssessmentsParams,
    formatAssessmentsResponse
} from "../utils/installments-assessment-utils";

export function useAssessments() {
    const [assessments, setAssessments] = useState([]);
    const [pagination, setPagination] = useState({
        totalElements: 0,
        totalPages: 0,
        page: 0,
        size: ASSESSMENT_DEFAULT_PAGE_SIZE
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statistics, setStatistics] = useState(null);

    const fetchAssessments = useCallback(async ({
        cycleId,
        status,
        filters = {},
        page = 0,
        size = ASSESSMENT_DEFAULT_PAGE_SIZE,
        sort = "dataApuracao,desc"
    } = {}) => {
        try {
            setLoading(true);
            setError(null);

            const {
                accessToken,
                clientId
            } = getAuthParams();

            const params = buildAssessmentsParams({
                cycleId,
                status,
                filters,
                page,
                size,
                sort
            });

            const response = await fetch(
                process.env.REACT_APP_ESSILOR_API +
                `pacto-rebates-apuracao/v1/apuracoes?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "access_token": accessToken,
                        "client_id": clientId
                    }
                }
            );

            if (!response.ok) {
                await throwResponseError(
                    response,
                    "Não foi possível buscar as apurações de parcelas."
                );
            }

            const data = await response.json();
            const formattedResponse = formatAssessmentsResponse(data);

            setAssessments(formattedResponse.items);
            setPagination(formattedResponse.pagination);
            setStatistics(formattedResponse.statistics);

            return {
                ok: true,
                assessments: formattedResponse.items,
                pagination: formattedResponse.pagination,
                statistics: formattedResponse.statistics,
                empty: formattedResponse.items.length === 0
            };
        } catch (requestError) {
            const errorMessage =
                requestError.message ||
                "Não foi possível buscar as apurações de parcelas.";

            setAssessments([]);
            setError(errorMessage);
            setStatistics(null);

            return {
                ok: false,
                reason: "request",
                error: errorMessage
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const clearAssessments = () => {
        setAssessments([]);
        setPagination({
            totalElements: 0,
            totalPages: 0,
            page: 0,
            size: ASSESSMENT_DEFAULT_PAGE_SIZE
        });
        setError(null);
        setStatistics(null);
    };

    return {
        assessments,
        pagination,
        statistics,
        loading,
        error,
        refetch: fetchAssessments,
        clearAssessments
    };
}